// Stage Mail-V2 (非 AI 接入): useMessageAction — 真实 message 状态操作。
//
// 设计(plan §C.2):
//   1. snapshot `prev` 当前 message 状态(由调用方提供 onOptimistic)
//   2. optimistic 应用 onOptimistic(messageId, partial)
//   3. fire PATCH /messages/<id> 或后端 batch
//   4. success → 保持 optimistic(后端 DTO 与 optimistic 一致)
//   5. failure → revert to prev + state[id]='error' + onSettled(id, ok=false)
//
// 适用 actions(非 AI):
//   - mark_read / mark_unread
//   - star / unstar
//   - archive / trash / spam(true|false) / unspam
//   - move(mailbox)
// AI 范围(不要)用 useAiReplyStream。
//
// 当前实现只覆盖 mark_read / mark_unread / star / unstar / archive / trash / spam / unspam / move。
// Label add/remove 由 useLabels 单独走。

import { useCallback, useRef, useState } from "react";
import { mailV2Api } from "../../../api/mail-v2";

export type MessageActionKind =
  | "mark_read"
  | "mark_unread"
  | "star"
  | "unstar"
  // 以下会在 Step 5/6/7 (后端扩展 PATCH whitelist 后)接入。
  | "archive"
  | "trash"
  | "spam"
  | "unspam"
  | "restore_to_inbox"
  | "move";

export interface MessageActionInput {
  kind: MessageActionKind;
  mailboxName?: string; // 仅 kind === "move" 用
}

export type ActionState = "idle" | "pending" | "success" | "error";

export interface UseMessageActionOptions {
  // 收到 id + 下次 partial,直接改 page 顶层 messages state;
  // 用 ref-of-latest-state 的模式不会触发 stale closure。
  applyOptimistic?: (messageId: string, partial: Record<string, unknown>) => void;
  // 服务器确认 success 后调用;推荐在这儿触发 messages + folderCounts refresh。
  onSettled?: (messageId: string, ok: boolean, error?: string) => void;
  // 出错时把 state 恢复 — applyOptimistic(prev) 后再调用 onSettled(id, false)
  revertOptimistic?: (messageId: string) => void;
}

export function useMessageAction(opts: UseMessageActionOptions = {}) {
  const [state, setState] = useState<Record<string, ActionState>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  // 防双击双发:同一 id + 同 kind 在飞行中不做第二次。
  const inFlightRef = useRef<Set<string>>(new Set());

  const run = useCallback(
    async (messageId: string, input: MessageActionInput): Promise<void> => {
      const key = `${messageId}:${input.kind}:${input.mailboxName ?? ""}`;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);

      // 1) optimistic apply
      const partial = _toPartial(input);
      opts.applyOptimistic?.(messageId, partial);
      setState((s) => ({ ...s, [messageId]: "pending" }));
      setErrors((es) => ({ ...es, [messageId]: null }));

      try {
        // mark_read / mark_unread / star / unstar 走现成 controller endpoint(内有 IMAP writeback)。
        // archive / trash / spam / unspam / move 走 /messages/<id>/action — 真实 IMAP MOVE,不允许只改 DB。
        if (input.kind === "mark_read") {
          await mailV2Api.markRead(messageId, true);
        } else if (input.kind === "mark_unread") {
          await mailV2Api.markRead(messageId, false);
        } else if (input.kind === "star") {
          await mailV2Api.toggleStar(messageId);
        } else if (input.kind === "unstar") {
          await mailV2Api.toggleStar(messageId);
        } else if (input.kind === "archive") {
          await mailV2Api.messageAction(messageId, { action: "archive" });
        } else if (input.kind === "trash") {
          await mailV2Api.messageAction(messageId, { action: "trash" });
        } else if (input.kind === "spam") {
          await mailV2Api.messageAction(messageId, { action: "spam" });
        } else if (input.kind === "unspam") {
          await mailV2Api.messageAction(messageId, { action: "unspam" });
        } else if (input.kind === "restore_to_inbox") {
          await mailV2Api.messageAction(messageId, { action: "restore_to_inbox" });
        } else if (input.kind === "move") {
          if (!input.mailboxName) throw new Error("kind=move requires mailboxName");
          await mailV2Api.messageAction(messageId, { action: "move", target_mailbox: input.mailboxName });
        }
        setState((s) => ({ ...s, [messageId]: "success" }));
        opts.onSettled?.(messageId, true);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        opts.revertOptimistic?.(messageId);
        setState((s) => ({ ...s, [messageId]: "error" }));
        setErrors((es) => ({ ...es, [messageId]: msg }));
        opts.onSettled?.(messageId, false, msg);
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [opts],
  );

  return { run, state, errors };
}

function _toPartial(input: MessageActionInput): Record<string, unknown> {
  switch (input.kind) {
    case "mark_read":
      return { isRead: true };
    case "mark_unread":
      return { isRead: false };
    case "star":
      return { isStarred: true };
    case "unstar":
      return { isStarred: false };
    case "archive":
    case "trash":
    case "spam":
    case "unspam":
    case "restore_to_inbox":
    case "move":
      return {};
    default:
      return {};
  }
}
